import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, TrendingUp, Target, Award, RefreshCw, Loader2, ChevronDown, ChevronUp, Printer,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { printReport } from "@/lib/printReport";
import { usePrintTemplate } from "@/hooks/usePrintTemplate";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Error";
  if (typeof d === "string") return d;
  return d.message_en || d.message_ar || JSON.stringify(d);
};

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pctBar = (pct) => {
  if (pct == null) return null;
  const w = Math.min(100, Math.max(0, pct));
  const tone = pct >= 100 ? "bg-emerald-500" : pct >= 75 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-2 rounded-full bg-[var(--surface-3)] overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${w}%` }} />
      </div>
      <span className="text-xs num-display font-semibold w-12 text-end">{pct.toFixed(1)}%</span>
    </div>
  );
};

export default function EmployeePerformanceReport({ range, branch }) {
  const { t, lang } = useLanguage();
  const template = usePrintTemplate();
  const isAr = lang === "ar";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [source, setSource] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (range?.start) q.set("start", range.start);
      if (range?.end) q.set("end", range.end);
      if (branch) q.set("branch", branch);
      if (source) q.set("source", source);
      const r = await api.get(`/reports/employee-performance?${q}`);
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.start, range?.end, branch, source]);

  const totals = data?.totals || {};
  const rows = data?.rows || [];

  const print = () => {
    if (!data) return;
    printReport({
      title: t("employee_performance"),
      subtitle: isAr ? "أداء الموظفين مقابل أهداف المبيعات" : "Staff performance vs sales targets",
      lang,
      template,
      meta: [
        { label: isAr ? "الفترة" : "Period", value: `${range?.start || "—"} → ${range?.end || "—"}` },
      ],
      columns: [
        isAr ? "الموظف" : "Employee",
        isAr ? "الهدف" : "Target",
        isAr ? "المبيعات" : "Sales",
        isAr ? "الإنجاز" : "Achievement",
        isAr ? "العمولة" : "Commission",
        isAr ? "الإكراميات" : "Tips",
        isAr ? "العملاء" : "Customers",
      ],
      rows: rows.map((r) => [
        r.employee_name,
        { value: fmt(r.period_target), className: "num" },
        { value: fmt(r.total_sales), className: "num" },
        { value: r.achievement_pct != null ? `${r.achievement_pct}%` : "—", className: "num" },
        { value: fmt(r.commission), className: "num" },
        { value: fmt(r.total_tips), className: "num" },
        { value: String(r.customers_served || 0), className: "num" },
      ]),
      totals: [
        { label: isAr ? "إجمالي المبيعات" : "Total sales", value: fmt(totals.total_sales), className: "gold" },
        { label: isAr ? "متوسط الإنجاز" : "Avg achievement", value: totals.avg_achievement_pct != null ? `${totals.avg_achievement_pct}%` : "—" },
      ],
    });
  };

  return (
    <div className="space-y-5" data-testid="employee-performance-report">
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <div className="inline-flex rounded-md border border-[var(--border)] bg-[var(--bg)] p-0.5" data-testid="perf-source-toggle">
          {[
            { v: "all", l_ar: "الكل", l_en: "All" },
            { v: "invoices", l_ar: "الفواتير", l_en: "Invoices" },
            { v: "bookings", l_ar: "الحجوزات", l_en: "Bookings" },
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => setSource(o.v)}
              data-testid={`perf-source-${o.v}`}
              className={`px-3 py-1 rounded text-xs ${source === o.v ? "bg-[var(--gold)] text-white" : "text-[var(--text-muted)]"}`}
            >
              {isAr ? o.l_ar : o.l_en}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={print} disabled={!data} className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <Printer size={14}/>{isAr ? "طباعة" : "Print"}
          </button>
          <button type="button" onClick={load} disabled={loading} className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2" data-testid="perf-refresh">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {t("refresh")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase text-[var(--text-muted)] flex items-center gap-1"><Users size={11}/>{isAr ? "الموظفون" : "Staff"}</div>
          <div className="text-2xl font-bold mt-1">{totals.staff_count || 0}</div>
        </div>
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase text-[var(--text-muted)] flex items-center gap-1"><TrendingUp size={11}/>{isAr ? "المبيعات" : "Sales"}</div>
          <div className="text-2xl font-bold mt-1 num-display">{fmt(totals.total_sales)}</div>
        </div>
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase text-[var(--text-muted)] flex items-center gap-1"><Target size={11}/>{isAr ? "الهدف" : "Target"}</div>
          <div className="text-2xl font-bold mt-1 num-display">{fmt(totals.period_target)}</div>
        </div>
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase text-[var(--text-muted)] flex items-center gap-1"><Award size={11}/>{isAr ? "متوسط الإنجاز" : "Avg achievement"}</div>
          <div className="text-2xl font-bold mt-1 gold-text num-display">
            {totals.avg_achievement_pct != null ? `${totals.avg_achievement_pct}%` : "—"}
          </div>
        </div>
        <div className="card-surface p-4">
          <div className="text-[10px] uppercase text-[var(--text-muted)]">{isAr ? "حقق الهدف" : "At target"}</div>
          <div className="text-2xl font-bold mt-1">
            {totals.staff_at_target || 0}
            <span className="text-sm text-[var(--text-muted)]"> / {totals.staff_with_target || 0}</span>
          </div>
        </div>
      </div>

      <p className="text-xs text-[var(--text-muted)]">
        {isAr
          ? "الهدف الشهري يُعدّ على الموظف في HR ← الراتب. للفترات المخصصة يُحسب الهدف تناسبياً بالأيام."
          : "Monthly sales target is set per employee under HR → Salary. Custom date ranges prorate the target by day."}
      </p>

      <div className="card-surface overflow-x-auto">
        <table className="table-dark min-w-full text-sm">
          <thead>
            <tr>
              <th className="w-8" />
              <th>{isAr ? "الموظف" : "Employee"}</th>
              <th className="text-end">{isAr ? "الهدف" : "Target"}</th>
              <th className="text-end">{isAr ? "المبيعات" : "Sales"}</th>
              <th>{isAr ? "الإنجاز" : "Achievement"}</th>
              <th className="text-end">{isAr ? "الفجوة" : "Gap"}</th>
              <th className="text-end">{isAr ? "العمولة" : "Commission"}</th>
              <th className="text-end">{t("tip")}</th>
              <th className="text-end">{isAr ? "العملاء" : "Customers"}</th>
              <th className="text-end">{isAr ? "البنود" : "Items"}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const key = r.employee_user_id || r.employee_id || `row-${idx}`;
              const ex = expanded[key];
              const gap = r.target_gap || 0;
              return (
                <React.Fragment key={key}>
                  <tr data-testid={`perf-row-${key}`}>
                    <td>
                      {(r.services || []).length > 0 && (
                        <button type="button" className="p-1" onClick={() => setExpanded((p) => ({ ...p, [key]: !p[key] }))}>
                          {ex ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                        </button>
                      )}
                    </td>
                    <td>
                      <div className="font-medium">{r.employee_name}</div>
                      {r.employee_code && <div className="text-[10px] text-[var(--text-muted)]">{r.employee_code}</div>}
                      {!r.has_login && (
                        <div className="text-[10px] text-amber-600">{isAr ? "بدون حساب نظام" : "No system login"}</div>
                      )}
                      {r.monthly_sales_target > 0 && (
                        <div className="text-[10px] text-[var(--text-muted)]">
                          {isAr ? "هدف شهري" : "Monthly"}: {fmt(r.monthly_sales_target)}
                        </div>
                      )}
                    </td>
                    <td className="text-end num-display">{fmt(r.period_target)}</td>
                    <td className="text-end num-display font-semibold">{fmt(r.total_sales)}</td>
                    <td>{pctBar(r.achievement_pct)}</td>
                    <td className={`text-end num-display ${gap >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                      {gap >= 0 ? "+" : ""}{fmt(gap)}
                    </td>
                    <td className="text-end num-display">{fmt(r.commission)}</td>
                    <td className="text-end num-display">{fmt(r.total_tips)}</td>
                    <td className="text-end num-display">{r.customers_served || 0}</td>
                    <td className="text-end num-display">{r.items || 0}</td>
                  </tr>
                  {ex && (r.services || []).length > 0 && (
                    <tr className="bg-[var(--bg)]/50">
                      <td colSpan={10} className="p-3">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-[var(--text-muted)]">
                              <th className="text-start py-1">{isAr ? "الخدمة" : "Service"}</th>
                              <th className="text-end py-1">{isAr ? "العدد" : "Count"}</th>
                              <th className="text-end py-1">%</th>
                              <th className="text-end py-1">{isAr ? "المبيعات" : "Sales"}</th>
                              <th className="text-end py-1">{isAr ? "العمولة" : "Commission"}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.services.map((s) => (
                              <tr key={s.name} className="border-t border-[var(--border-subtle)]">
                                <td className="py-1">{s.name}</td>
                                <td className="text-end py-1 num-display">{s.count}</td>
                                <td className="text-end py-1 num-display">{s.percent}%</td>
                                <td className="text-end py-1 num-display">{fmt(s.sales)}</td>
                                <td className="text-end py-1 num-display">{fmt(s.commission)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={10} className="text-center py-10 text-[var(--text-muted)]">{isAr ? "لا توجد بيانات" : "No data for this period"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
